import React, { useState } from "react";
import { useDisclosure, useToast } from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import { useMeeting } from "./hooks/useMeeting";
import { LandingView } from "./components/LandingView";
import { MeetingInviteView } from "./components/MeetingInvite";
import { AttendeeCheckinModal } from "./components/AttendeeCheckin";
import { MeetingModal } from "./components/MeetingModal";
import { OverallAwardView, PreviewScoresView } from "./components/OverallAward";
import { SoloAwardView } from "./components/SoloAward";
import { MeetingOverrideModal } from "./components/MeetingOverride";
import { ReopenMeetingModal } from "./components/MeetingOverride/ReopenMeetingModal";
import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";

type ActiveView =
  | "landing"
  | "invite"
  | "checkin"
  | "meeting"
  | "overall"
  | "preview"
  | "solo";

interface PanelMeetingStepProps {
  evalId?: number;
  isRfxLead?: boolean;
  rfxId: number;
  rfxSubmissionId?: number;
}

export const PanelMeetingStep: React.FC<PanelMeetingStepProps> = ({
  rfxId,
  rfxSubmissionId: _rfxSubmissionId,
  evalId,
  isRfxLead = false,
}) => {
  const [activeView, setActiveView] = useState<ActiveView>("landing");
  const { rfxData } = useRfxData();

  const toast = useToast();
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);

  const {
    isOpen: isCheckinOpen,
    onOpen: onCheckinOpen,
    onClose: onCheckinClose,
  } = useDisclosure();

  const {
    isOpen: isMeetingModalOpen,
    onOpen: onMeetingModalOpen,
    onClose: onMeetingModalClose,
  } = useDisclosure();

  const {
    isOpen: isOverrideOpen,
    onOpen: onOverrideOpen,
    onClose: onOverrideClose,
  } = useDisclosure();

  const {
    isOpen: isReopenOpen,
    onOpen: onReopenOpen,
    onClose: onReopenClose,
  } = useDisclosure();

  const { meeting, isLoading, createMeeting, completeMeeting, reopenMeeting } =
    useMeeting(rfxId, evalId);

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="panel-meeting-step-loading">
        <SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
        <SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
      </div>
    );
  }

  const handleScheduleMeeting = async () => {
    if (meeting) {
      setActiveView("invite");
    } else {
      // Auto-create a draft meeting (no schedule yet) to populate attendees from panel
      setIsCreatingDraft(true);
      try {
        await createMeeting.mutateAsync({});
        setActiveView("invite");
      } catch {
        toast({
          title: "Error",
          description: "Failed to initialise meeting. Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setIsCreatingDraft(false);
      }
    }
  };

  const handleStartMeeting = () => {
    onCheckinOpen();
  };

  const handleResumeMeeting = () => {
    onMeetingModalOpen();
    setActiveView("meeting");
  };

  const handlePreviewScores = () => {
    setActiveView("preview");
  };

  const handleViewResults = () => {
    setActiveView("overall");
  };

  const handleSkipMeeting = () => {
    onOverrideOpen();
  };

  const handleReopenMeeting = () => {
    onReopenOpen();
  };

  const handleReopenConfirm = async (reason: string) => {
    try {
      await reopenMeeting.mutateAsync({ reason });
      onReopenClose();
      // Meeting is now back to in_progress — open the meeting modal
      onMeetingModalOpen();
      setActiveView("meeting");
      toast({
        title: "Meeting Reopened",
        description: "The meeting has been reopened for further scoring.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to reopen meeting. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleCheckinMeetingStarted = () => {
    onCheckinClose();
    onMeetingModalOpen();
    setActiveView("meeting");
  };

  const handleMeetingModalClose = () => {
    onMeetingModalClose();
    setActiveView("landing");
  };

  const handleCompleteMeeting = async () => {
    try {
      await completeMeeting.mutateAsync();
      onMeetingModalClose();
      setActiveView("overall");
      toast({
        title: "Meeting Completed",
        description: "The panel meeting has been completed. You can now finalise award decisions.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to complete meeting. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleOverrideComplete = () => {
    onOverrideClose();
    setActiveView("solo");
  };

  if (activeView === "invite" && meeting && evalId != null) {
    return (
      <MeetingInviteView
        rfxId={rfxId}
        evalId={evalId}
        meeting={meeting}
        onBack={() => setActiveView("landing")}
        rfxLeadName={rfxData?.rfxLead?.fullName}
        rfxTitle={rfxData?.name}
      />
    );
  }

  if (activeView === "overall" && evalId != null) {
    return (
      <div data-testid="panel-meeting-step-overall">
        <button
          type="button"
          onClick={() => setActiveView("landing")}
          className="text-sm text-blue-600 hover:underline mb-4"
          data-testid="back-to-landing-btn"
        >
          &larr; Back to Meeting
        </button>
        <OverallAwardView rfxId={rfxId} evalId={evalId} />
      </div>
    );
  }

  if (activeView === "preview" && evalId != null) {
    return (
      <div data-testid="panel-meeting-step-preview">
        <button
          type="button"
          onClick={() => setActiveView("landing")}
          className="text-sm text-blue-600 hover:underline mb-4"
          data-testid="back-to-landing-btn"
        >
          &larr; Back
        </button>
        <PreviewScoresView rfxId={rfxId} evalId={evalId} />
      </div>
    );
  }

  if (activeView === "solo" && evalId != null) {
    return (
      <div data-testid="panel-meeting-step-solo">
        <button
          type="button"
          onClick={() => setActiveView("landing")}
          className="text-sm text-blue-600 hover:underline mb-4"
          data-testid="back-to-landing-btn"
        >
          &larr; Back
        </button>
        <SoloAwardView rfxId={rfxId} evalId={evalId} />
      </div>
    );
  }

  return (
    <div data-testid="panel-meeting-step">
      <LandingView
        isRfxLead={isRfxLead}
        isLoading={isCreatingDraft}
        meeting={meeting}
        onScheduleMeeting={handleScheduleMeeting}
        onStartMeeting={handleStartMeeting}
        onResumeMeeting={handleResumeMeeting}
        onPreviewScores={handlePreviewScores}
        onSkipMeeting={handleSkipMeeting}
        onViewResults={handleViewResults}
        onReopenMeeting={handleReopenMeeting}
        isReopening={reopenMeeting.isPending}
      />

      {evalId != null && meeting && (
        <AttendeeCheckinModal
          rfxId={rfxId}
          evalId={evalId}
          isOpen={isCheckinOpen}
          onClose={onCheckinClose}
          onMeetingStarted={handleCheckinMeetingStarted}
        />
      )}

      {evalId != null && meeting && (
        <MeetingModal
          isOpen={isMeetingModalOpen}
          onClose={handleMeetingModalClose}
          rfxId={rfxId}
          evalId={evalId}
          onCompleteMeeting={handleCompleteMeeting}
          isCompleting={completeMeeting.isPending}
        />
      )}

      {evalId != null && meeting && isRfxLead && (
        <MeetingOverrideModal
          rfxId={rfxId}
          evalId={evalId}
          isOpen={isOverrideOpen}
          onClose={onOverrideClose}
          onOverrideComplete={handleOverrideComplete}
        />
      )}

      {evalId != null && isRfxLead && (
        <ReopenMeetingModal
          isOpen={isReopenOpen}
          onClose={onReopenClose}
          onConfirm={handleReopenConfirm}
          isSubmitting={reopenMeeting.isPending}
        />
      )}
    </div>
  );
};
